import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { supabase } from './supabase/supabaseClient.js';
import { useTranslation } from 'react-i18next';
import {
  FaBoxOpen, FaUsers, FaTools, FaPhone, FaTimes,
  FaMailBulk, FaLightbulb, FaNewspaper, FaTrophy, //FaHandshake,
  FaFacebook, FaLine, FaMapMarkerAlt, FaFileInvoice, FaPhoneAlt, 
  FaEnvelope, FaClock, FaGlobe, FaChevronLeft, FaChevronRight,
  FaFilePdf, FaArrowRight, FaCalendarAlt, FaCheckCircle, FaImages
} from 'react-icons/fa';
import './App.css';
import { getNewsImages } from './newsImages.js';
import ScrollToTop from './ScrollToTop.js';
import Contact from './contact.js';
import About from './about.js';
import Services from './services.js';
import Products from './products.js';
import AdminPage from './Admin/AdminPage.js';
import CookieConsent from './CookieConsent.js';
import Navbar from './Navbar.js';
import Footer from './Footer.js';
import ProductDetail from './ProductDetail.js';
import FloatingContact from './FloatingContact.js';
// ============================================================================
// FONT IMPORT & THEME
// ============================================================================
const fontStyle = document.createElement('style');
fontStyle.innerHTML = `
  @import url('https://fonts.googleapis.com/css2?family=Prompt:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap');
  * { font-family: 'Prompt', sans-serif; }
  body { margin: 0; -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }
`;
document.head.appendChild(fontStyle);

const THEME = {
  primary: '#216488',
  secondary: '#155e75',
  accent: '#ffa200',
  gradient: 'linear-gradient(135deg, #ffffff, #06b6d4)', 
  text: '#1e293b',
  bgLight: '#f8fafc',
  bgGray: '#f3f4f6'
};

// 🛑 คอมเมนต์ส่วนนี้ไว้ชั่วคราว
/*
const TRUSTED_CLIENTS = [
  { id: 1, name: 'Hospital A', logo: '/assets/client1.png' }, { id: 2, name: 'Hospital B', logo: '/assets/client2.png' },
  { id: 3, name: 'Clinic C', logo: '/assets/client3.png' }, { id: 4, name: 'Hospital A', logo: '/assets/client1.png' },
  { id: 5, name: 'Hospital B', logo: '/assets/client2.png' }, { id: 6, name: 'Clinic C', logo: '/assets/client3.png' }
];
*/

const isPdf = (url) => url && url.toLowerCase().includes('.pdf');

function useKeyDown(key, handler) {
  useEffect(() => {
    const listener = (event) => { if (event.key === key) handler(event); };
    document.addEventListener("keydown", listener);
    return () => document.removeEventListener("keydown", listener);
  }, [key, handler]);
}

// ============================================================================
// NEWS GALLERY — แกลเลอรีรูปภาพหลายรูปในข่าวเดียว (ใช้ในโมดัลข่าว)
// ============================================================================
const NewsGallery = ({ news, t, currentLang, startIndex = 0 }) => {
  const images = getNewsImages(news);
  const list = images.length > 0 ? images : ['/assets/news1.jpg'];
  const hasMany = list.length > 1;

  // โมดัลถูก unmount ทุกครั้งที่ปิด จึงเริ่มที่รูปที่ผู้ใช้กดมาได้เลย
  const [index, setIndex] = useState(Math.min(Math.max(startIndex, 0), list.length - 1));

  const goNext = useCallback(() => setIndex(i => (i + 1) % list.length), [list.length]);
  const goPrev = useCallback(() => setIndex(i => (i - 1 + list.length) % list.length), [list.length]);

  useKeyDown('ArrowRight', goNext);
  useKeyDown('ArrowLeft', goPrev);

  const navBtnStyle = (side) => ({
    position: 'absolute', top: '50%', [side]: '12px', transform: 'translateY(-50%)',
    width: '42px', height: '42px', borderRadius: '50%', border: 'none',
    background: 'rgba(255,255,255,0.92)', color: THEME.primary, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem',
    boxShadow: '0 4px 15px rgba(0,0,0,0.25)', transition: 'all 0.2s ease', zIndex: 3
  });

  return (
    // ล็อกความสูงกล่องไว้ แล้วแบ่งพื้นที่ด้วย flex — รูปได้พื้นที่ที่เหลือทั้งหมด
    // ส่วนข้อความยาวจะเลื่อนอยู่ในกรอบตัวเอง ไม่ดันให้ทั้งโมดัลยืด
    <div className="news-modal-content news-gallery" style={{ background: 'white', borderRadius: '16px', width: '92%', maxWidth: '940px', height: '86vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 0 }}>

      {/* เวทีรูปหลัก — กินพื้นที่ที่เหลือทั้งหมด */}
      <div className="news-gallery-stage" style={{ position: 'relative', flex: '1 1 auto', minHeight: 0, background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <img
          src={list[index]}
          alt={`${news.title} (${index + 1}/${list.length})`}
          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
          onError={(e) => { e.target.src = 'https://via.placeholder.com/800x600?text=No+Image'; }}
        />

        {hasMany && (
          <>
            <button onClick={goPrev} aria-label="รูปก่อนหน้า" style={navBtnStyle('left')}><FaChevronLeft /></button>
            <button onClick={goNext} aria-label="รูปถัดไป" style={navBtnStyle('right')}><FaChevronRight /></button>
            {/* มุมล่างซ้าย เลี่ยงปุ่มปิดที่ลอยอยู่มุมขวาบน */}
            <span style={{ position: 'absolute', bottom: '12px', left: '14px', background: 'rgba(15,23,42,0.7)', color: 'white', padding: '4px 12px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px', zIndex: 3 }}>
              <FaImages /> {index + 1} / {list.length}
            </span>
          </>
        )}
      </div>

      {/* แถบรูปย่อ — ความสูงคงที่ ไม่ยืด ไม่หด */}
      {hasMany && (
        <div style={{ flex: '0 0 auto', display: 'flex', gap: '8px', padding: '10px 14px', overflowX: 'auto', background: '#f1f5f9', borderTop: '1px solid #e2e8f0' }}>
          {list.map((img, i) => (
            <img
              key={`${img}-${i}`}
              src={img}
              alt={`thumb-${i + 1}`}
              onClick={() => setIndex(i)}
              style={{
                width: '68px', height: '50px', objectFit: 'cover', borderRadius: '6px', cursor: 'pointer',
                flexShrink: 0,
                border: i === index ? `2px solid ${THEME.primary}` : '2px solid transparent',
                opacity: i === index ? 1 : 0.5,
                transition: 'all 0.2s ease'
              }}
            />
          ))}
        </div>
      )}

      {/* รายละเอียดข่าว — สูงไม่เกิน 34% ของโมดัล ถ้าข้อความยาวจะเลื่อนในกรอบนี้ */}
      <div className="news-modal-details" style={{ flex: '0 0 auto', maxHeight: '34%', overflowY: 'auto', padding: '18px 26px 22px', borderTop: '1px solid #e2e8f0' }}>
        <span className="news-date" style={{ color: THEME.secondary, fontSize: '0.9rem', display: 'inline-flex', alignItems: 'center', gap: '7px', marginBottom: '0.5rem', fontWeight: 600 }}>
          <FaCalendarAlt /> {news.date}
        </span>
        <h3 style={{ margin: '0 0 0.6rem 0', color: THEME.primary, fontSize: '1.45rem', lineHeight: 1.3 }}>
          {news[`title_${currentLang}`] || news.title}
        </h3>
        <p style={{ lineHeight: '1.75', color: '#475569', fontSize: '1rem', whiteSpace: 'pre-line', margin: 0 }}>
          {news[`description_${currentLang}`] || news.description}
        </p>
        {hasMany && (
          <p style={{ marginTop: '0.9rem', marginBottom: 0, color: '#94a3b8', fontSize: '0.82rem' }}>
            {t('useArrowKeys', 'ใช้ปุ่มลูกศร ← → บนคีย์บอร์ดเพื่อเลื่อนดูรูปได้')}
          </p>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// MODAL
// ============================================================================
const ImageModal = ({ image, onClose, type = 'certificate', t, currentLang, galleryStart = 0 }) => {
  const [opacity, setOpacity] = useState(0);
  useEffect(() => { if (image) setTimeout(() => setOpacity(1), 50); else setOpacity(0); }, [image]);
  useKeyDown('Escape', onClose);
  if (!image) return null;
  const isContentPdf = isPdf(image.image);
  return (
    <div className="modal-backdrop" onClick={onClose} style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0, 0, 0, 0.9)', zIndex: 9999, opacity: opacity, transition: 'opacity 0.3s ease-in-out', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0' }}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ position: 'relative', transform: opacity ? 'scale(1)' : 'scale(0.95)', transition: 'transform 0.3s ease', maxWidth: '100vw', maxHeight: '100vh', width: 'auto', height: 'auto', background: 'transparent', boxShadow: 'none', border: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <button className="modal-close-btn" onClick={onClose} style={{ position: 'fixed', top: '20px', right: '20px', cursor: 'pointer', background: 'rgba(0,0,0,0.5)', borderRadius: '50%', width: '50px', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid white', color: 'white', fontSize: '1.5rem', zIndex: 10000 }}><FaTimes /></button>
        
        {type === 'certificate' ? (
          <div style={{ textAlign: 'center', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            {isContentPdf ? <iframe src={image.image} title={image.title} style={{ width: '90vw', height: '95vh', border: 'none', borderRadius: '8px', backgroundColor: 'white' }} /> : <img src={image.image} alt={image.title} style={{ maxHeight: '95vh', maxWidth: '95vw', objectFit: 'contain', boxShadow: '0 0 20px rgba(0,0,0,0.5)', backgroundColor: 'white', borderRadius: '4px' }} />}
            <h3 style={{ marginTop: '10px', color: 'white', textShadow: '0 2px 4px rgba(0,0,0,0.8)', fontSize: '1.2rem', position: 'absolute', bottom: '20px', background: 'rgba(0,0,0,0.6)', padding: '5px 15px', borderRadius: '20px' }}>{image.title} {isContentPdf && '(PDF Document)'}</h3>
          </div>
        ) : type === 'service' ? (
          <div className="service-modal-content" style={{ background: 'white', borderRadius: '20px', padding: '40px', maxWidth: '800px', width: '95%', position: 'relative', textAlign: 'center', boxShadow: '0 20px 50px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto' }}>
             
             {/* 🟢 แก้ไขตรงนี้: เปลี่ยนจากการใช้ <img src={...}/> เป็นการเรียกใช้ {image.icon} */}
             <div style={{ width: '100%', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', borderRadius: '15px', padding: '40px 20px', color: THEME.primary }}>
                <div style={{ transform: 'scale(1.8)' }}> 
                  {/* ขยายขนาด Icon ให้ใหญ่ขึ้นเพื่อให้สมดุลกับพื้นที่ Modal */}
                  {image.icon}
                </div>
             </div>

             <h2 style={{ color: THEME.primary, marginBottom: '1.5rem', fontSize: '2rem' }}>{image.title}</h2>
             <p style={{ fontSize: '1.2rem', color: '#64748b', lineHeight: '1.8', marginBottom: '2rem' }}>{image.description}</p>
             <div style={{ background: '#f0f9ff', padding: '25px', borderRadius: '15px', marginBottom: '2rem', textAlign: 'left' }}><h4 style={{ color: THEME.secondary, marginBottom: '15px', fontSize: '1.2rem', display: 'flex', alignItems: 'center', gap: '8px' }}><FaCheckCircle /> {t('whatYouGet')}</h4><ul style={{ listStyle: 'none', padding: 0 }}><li style={{ padding: '8px 0', color: '#444', borderBottom: '1px dashed #cbd5e1' }}>• {t('serviceDetail1')}</li><li style={{ padding: '8px 0', color: '#444', borderBottom: '1px dashed #cbd5e1' }}>• {t('serviceDetail2')}</li><li style={{ padding: '8px 0', color: '#444', borderBottom: '1px dashed #cbd5e1' }}>• {t('serviceDetail3')}</li><li style={{ padding: '8px 0', color: '#444' }}>• {t('serviceDetail4')}</li></ul></div>
             <div style={{ marginTop: '1rem' }}><Link to="/contact" className="cta-btn primary" onClick={onClose} style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', padding: '14px 40px', background: THEME.gradient, color: 'white', borderRadius: '50px', textDecoration: 'none', fontWeight: 'bold', fontSize: '1.1rem', boxShadow: '0 8px 20px rgba(0,0,0,0.2)' }}><FaPhone /> {t('contactForService')}</Link></div>
          </div>
        ) : (
          <NewsGallery news={image} t={t} currentLang={currentLang} startIndex={galleryStart} />
        )}
      </div>
    </div>
  );
};

// ============================================================================
// PAGE SECTIONS
// ============================================================================

const HeroSection = ({ t }) => {
  if (!t) return null;
  return (
    <section className="hero" style={{ 
      minHeight: '100vh',
      width: '100vw',
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'flex-start', 
      overflow: 'hidden', 
      position: 'relative',
      paddingLeft: '5%', 
      paddingRight: '5%',
      backgroundImage: 'url("/assets/company4.jpg")',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      marginTop: '-120px'
    }}>
{/* 🟢 Overlay สีที่ค่อยๆ โปร่งใสลงด้านล่าง */}
      <div style={{ 
        position: 'absolute', 
        top: 0, 
        left: 0, 
        width: '100%', 
        height: '100%', 
        /* ไล่เฉดสีจากบนลงล่าง (to bottom):
           0% (บนสุด) : สีน้ำเงินเข้ม ทึบ 95% (เพื่อให้ตัวหนังสืออ่านง่าย)
           50% (ตรงกลาง) : สีเริ่มจางลง ทึบแค่ 50%
           100% (ล่างสุด) : โปร่งใส 100% (โชว์รูปภาพแบบไม่มีสีบัง)
        */
        background: 'linear-gradient(to bottom, rgba(14, 116, 144, 0.95) 0%, rgba(8, 145, 178, 0.50) 50%, rgba(8, 145, 178, 0) 100%)', 
        zIndex: 1 
      }} />
      <div 
        className="hero-content" 
        style={{ 
          position: 'relative', 
          zIndex: 2, 
          color: 'white',
          
          // 🔴 เพิ่มบรรทัดนี้เพื่อเลื่อนเนื้อหาลงมา
          // ปรับตัวเลข 80px หรือ 100px ได้ตามความเหมาะสมที่คุณชอบครับ
          paddingTop: '650px', 
          
          paddingLeft: '5%', // ดันออกจากขอบซ้ายนิดหน่อย
        }}
      ></div>
      <div className="hero-content" style={{ position: 'relative', zIndex: 2, maxWidth: '800px', textAlign: 'left', color: 'white', padding: '20px', animation: 'fadeInLeft 1s ease-out' }}>
        <h1 style={{ fontWeight: 800, marginBottom: '1.5rem', lineHeight: 1, textShadow: '0 4px 20px rgba(0,0,0,0.6)', display: 'inline-flex', flexDirection: 'column' }}>
        {/* ส่วนของ ASCENT */}
        <span style={{ 
          fontSize: 'clamp(3.5rem, 8vw, 5.5rem)', 
          display: 'block', 
          letterSpacing: '2px' 
        }}>
          ASCENT
        </span>
        
        {/* ส่วนของ CORPORATION CO., LTD. */}
        <span style={{ 
          fontSize: 'clamp(3rem, 2.2vw, 1.4rem)',
          fontWeight: 600, 
          opacity: 0.95,
          display: 'flex',
          justifyContent: 'space-between', 
          width: '100%',
          marginTop: '0.2rem'
        }}>
          {"CORPORATION CO., LTD".split("").map((char, index) => (
            <span key={index}>{char === " " ? "\u00A0" : char}</span>
          ))}
        </span>
      </h1>

        <p style={{ fontSize: 'clamp(1.1rem, 2vw, 1.5rem)', fontWeight: 300, marginBottom: '2.5rem', opacity: 0.9, lineHeight: 1.6, maxWidth: '600px', textShadow: '0 2px 10px rgba(0,0,0,0.5)' }}>
          {t('heroSubtitle')}
        </p>

        <Link to="/products" style={{ textDecoration: 'none' }}>
          <button 
            style={{ 
              backgroundColor: THEME.primary, 
              color: 'white', 
              border: 'none', 
              padding: '14px 35px', 
              fontSize: '1.1rem', 
              fontWeight: 'bold', 
              borderRadius: '4px', 
              cursor: 'pointer', 
              boxShadow: '0 6px 20px rgba(0, 0, 0, 0.4)',
              transition: 'all 0.3s ease', 
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: '10px' 
            }} 
            onMouseEnter={(e) => { e.target.style.backgroundColor = THEME.secondary; e.target.style.transform = 'translateY(-3px)'; }} 
            onMouseLeave={(e) => { e.target.style.backgroundColor = THEME.primary; e.target.style.transform = 'translateY(0)'; }}
          >
            {t('heroButton')} <FaArrowRight />
          </button>
        </Link>
      </div>
    </section>
  );
};

const AboutSection = ({ t }) => (
  <section className="about-section" style={{ padding: '5rem 1.5rem', background: 'transparent' }}>
    <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '2.5rem' }}>
        
        {/* Card: เกี่ยวกับเรา */}
        <div 
          style={{ 
            background: 'rgba(255, 255, 255, 0.7)', 
            backdropFilter: 'blur(12px)',
            borderRadius: '12px', 
            padding: '3rem 2.5rem', 
            boxShadow: '0 10px 30px rgba(0, 123, 255, 0.05)', 
            borderTop: `6px solid #216488`, // 🔵 เปลี่ยนเป็นสี #216488
            transition: 'transform 0.3s ease', 
            display: 'flex', 
            flexDirection: 'column' 
          }} 
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'} 
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '1.5rem' }}>
            <div style={{ background: 'rgba(0, 123, 255, 0.1)', padding: '12px', borderRadius: '50%', color: '#216488' }}>
              <FaUsers size={28} /> {/* 🔵 ไอคอนสี #216488 */}
            </div>
            <h2 style={{ fontSize: '1.8rem', fontWeight: 700, color: '#1e293b', margin: 0 }}>{t('aboutUsTitle')}</h2>
          </div>
          <p style={{ fontSize: '1.1rem', lineHeight: '1.8', color: '#475569', flex: 1 }}>{t('aboutUsDesc')}</p>
        </div>

        {/* Card: วิสัยทัศน์ */}
        <div 
          style={{ 
            background: 'rgba(255, 255, 255, 0.7)', 
            backdropFilter: 'blur(12px)',
            borderRadius: '12px', 
            padding: '3rem 2.5rem', 
            boxShadow: '0 10px 30px rgba(0, 123, 255, 0.05)', 
            borderTop: `6px solid #216488`, // 🔵 เปลี่ยนเป็นสี #216488
            transition: 'transform 0.3s ease', 
            display: 'flex', 
            flexDirection: 'column' 
          }} 
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'} 
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '1.5rem' }}>
            <div style={{ background: 'rgba(0, 123, 255, 0.1)', padding: '12px', borderRadius: '50%', color: '#216488' }}>
              <FaLightbulb size={28} /> {/* 🔵 ไอคอนสี #216488 */}
            </div>
            <h2 style={{ fontSize: '1.8rem', fontWeight: 700, color: '#1e293b', margin: 0 }}>{t('visionTitle')}</h2>
          </div>
          <p style={{ fontSize: '1.1rem', lineHeight: '1.8', color: '#475569', flex: 1 }}>{t('visionDesc')}</p>
        </div>

      </div>
    </div>
  </section>
);

const ProductsSection = ({ t, products, currentLang }) => (
  <section className="full-section products-section" style={{ background: '#f9fafb' }}>
    <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
      <h2 style={{ color: THEME.primary, fontSize: '2.5rem', fontWeight: 'bold' }}>{t('recommendedProducts')}</h2>
      <div style={{ width: '80px', height: '5px', background: THEME.gradient, margin: '10px auto', borderRadius: '3px' }} />
    </div>
    
    <div className="products-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '2rem', maxWidth: '1200px', margin: '0 auto', padding: '0 1rem' }}>
      {products.length > 0 ? (
        products.map((product) => (
          /* 🟢 แก้ไขจุดนี้: เปลี่ยนจาก to="/products" เป็น path ของสินค้าตัวนั้นๆ */
          <Link key={product.id} to={`/product/${product.id}`} style={{ textDecoration: 'none' }}>
            <div className="product-card" style={{ background: 'white', borderRadius: '15px', overflow: 'hidden', boxShadow: '0 5px 15px rgba(0,0,0,0.08)', transition: 'transform 0.3s ease', height: '100%', display: 'flex', flexDirection: 'column' }} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
              <div style={{ position: 'relative', height: '220px', overflow: 'hidden', borderBottom: '1px solid #eee' }}>
                <img 
                  src={product.image || 'https://via.placeholder.com/300?text=No+Image'} 
                  alt={product.name} 
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                  loading="lazy" /* 🚀 ช่วยเรื่อง Performance */
                  onError={(e) => { e.target.src = 'https://via.placeholder.com/300?text=No+Image'; }} 
                />
                <div style={{ 
  position: 'absolute', 
  top: '10px', 
  left: '10px', 
  background: '#00aeef', // 🟢 เปลี่ยนแค่ตรงนี้เป็นสีฟ้า
  color: 'white', 
  padding: '3px 12px', 
  borderRadius: '15px', 
  fontSize: '0.8rem', 
  fontWeight: 'bold',
  zIndex: 2 
}}>
  {t('recommended')}
</div>
              </div>
              <div style={{ padding: '1.5rem', flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <div>
                  <span style={{ fontSize: '0.9rem', color: THEME.secondary, fontWeight: 600 }}>{product.category}</span>
                  <h3 style={{ fontSize: '1.2rem', color: THEME.text, margin: '0.5rem 0' }}>{product[`name_${currentLang}`] || product.name}</h3>
                </div>
                <div style={{ marginTop: '1rem', color: THEME.primary, fontWeight: 'bold', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '5px' }}>
                  {t('viewDetail')} <FaArrowRight size={12} />
                </div>
              </div>
            </div>
          </Link>
        ))
      ) : (
        <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem', color: '#94a3b8', background: 'white', borderRadius: '15px' }}>
          <FaBoxOpen size={40} style={{ marginBottom: '1rem', opacity: 0.5 }} />
          <p style={{ fontSize: '1.2rem' }}>{t('loadingProducts')}</p>
        </div>
      )}
    </div>

    {/* ปุ่มดูทั้งหมดด้านล่างยังคงไปที่หน้า /products เหมือนเดิมถูกต้องแล้วครับ */}
    <div style={{ textAlign: 'center', marginTop: '4rem' }}>
      <Link to="/products" style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', padding: '1rem 2.5rem', background: 'transparent', border: `2px solid ${THEME.primary}`, color: THEME.primary, borderRadius: '50px', fontSize: '1.1rem', fontWeight: 'bold', textDecoration: 'none', transition: 'all 0.3s ease' }} onMouseEnter={(e) => { e.target.style.background = THEME.primary; e.target.style.color = 'white'; }} onMouseLeave={(e) => { e.target.style.background = 'transparent'; e.target.style.color = THEME.primary; }}>
        {t('viewAllProducts')} <FaBoxOpen />
      </Link>
    </div>
  </section>
);

const ServicesSection = ({ t, onServiceClick }) => {
  // กำหนด Icons ที่จะใช้คู่กับแต่ละบริการ
  const services = [
    { 
      id: 1, 
      title: t('maintenanceService'), 
      description: t('maintenanceDesc'), 
      icon: <FaTools size={60} /> // เปลี่ยนจาก iconSrc เป็น icon component
    },
    { 
      id: 2, 
      title: t('technicalConsultation'), 
      description: t('technicalDesc'), 
      icon: <FaLightbulb size={60} /> 
    },
    { 
      id: 3, 
      title: t('distribution'), 
      description: t('distributionDesc'), 
      icon: <FaBoxOpen size={60} /> 
    }
  ];

  return (
    <section className="services-section full-section" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', background: '#ffffff', padding: '5rem 1.5rem' }}>
      <h2 style={{ color: THEME.primary, fontSize: '2.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px' }}>
        <FaTools /> {t('ourServices')}
      </h2>
      <div style={{ width: '80px', height: '5px', background: THEME.gradient, margin: '10px auto 3rem', borderRadius: '3px' }} />
      
      <div className="services-grid" style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
        gap: '2rem', 
        width: '100%', 
        maxWidth: '1200px' 
      }}>
        {services.map(service => (
          <div 
            className="service-card" 
            onClick={() => onServiceClick(service)} 
            key={service.id} 
            style={{ 
              transition: 'all 0.3s ease', 
              borderTop: `5px solid ${THEME.primary}`, // เปลี่ยนจาก borderBottom มาเป็น borderTop จะดูโมเดิร์นกว่า
              height: '100%', 
              padding: '3rem 2rem', 
              background: '#f8fafc', // ปรับสีพื้นหลังการ์ดเล็กน้อยให้ตัดกับพื้นหลังเซกชัน
              borderRadius: '15px', 
              boxShadow: '0 5px 15px rgba(0,0,0,0.05)', 
              cursor: 'pointer', 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center' 
            }} 
            onMouseEnter={(e) => { 
              e.currentTarget.style.transform = 'translateY(-10px)'; 
              e.currentTarget.style.boxShadow = '0 15px 30px rgba(0,0,0,0.1)';
              e.currentTarget.style.background = '#ffffff'; // เปลี่ยนสีตอน hover ให้ดูเด่นขึ้น
            }} 
            onMouseLeave={(e) => { 
              e.currentTarget.style.transform = 'translateY(0)'; 
              e.currentTarget.style.boxShadow = '0 5px 15px rgba(0,0,0,0.05)';
              e.currentTarget.style.background = '#f8fafc';
            }}
          >
            {/* แสดงผล Icon แทนรูปภาพ */}
            <div style={{ 
              marginBottom: '1.5rem', 
              color: THEME.primary, // ใช้สีหลักของแบรนด์
              background: 'white',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 10px rgba(0,0,0,0.05)'
            }}>
              {service.icon}
            </div>

            <h3 style={{ color: THEME.primary, fontSize: '1.5rem', marginBottom: '1rem' }}>{service.title}</h3>
            <p style={{ fontSize: '1.1rem', flexGrow: 1, color: '#64748b', lineHeight: '1.6' }}>{service.description}</p>
            <div style={{ marginTop: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: THEME.secondary, fontWeight: 'bold' }}>
              {t('viewDetail')} <FaArrowRight />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

const CertificatesSection = ({ t, certificates, certLoading, onCertificateClick }) => (
  <section className="certificates-section full-section" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
    <h2 style={{ color: THEME.primary, fontSize: '2.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px' }}>
      <FaTrophy /> {t('certification')}
    </h2>
    <p className="certificates-subtitle" style={{ fontSize: '1.2rem', marginBottom: '1rem' }}>{t('certSubtitle')}</p>
    <div style={{ width: '80px', height: '5px', background: THEME.gradient, margin: '0 auto 3rem', borderRadius: '3px' }} />
    {certLoading ? (
      <div style={{ textAlign: 'center', padding: '2rem', color: THEME.accent }}>{t('loading')}</div>
    ) : certificates.length === 0 ? (
      <div style={{ textAlign: 'center', padding: '2rem', color: THEME.accent }}>{t('noData')}</div>
    ) : (
      <div className="certificates-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem', justifyContent: 'center', width: '100%', maxWidth: '1200px' }}>
        {certificates.map((cert) => (
          <CertificateCard key={cert.id} cert={cert} t={t} onClick={() => onCertificateClick(cert)} />
        ))}
      </div>
    )}
  </section>
);

const CertificateCard = ({ cert, t, onClick }) => {
  const [isHovered, setIsHovered] = useState(false);
  const isContentPdf = isPdf(cert.image);
  return (
    <div className="certificate-card" onClick={onClick} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} style={{ cursor: 'pointer', transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)', transform: isHovered ? 'translateY(-5px)' : 'translateY(0)', boxShadow: isHovered ? '0 14px 28px rgba(0,0,0,0.15), 0 10px 10px rgba(0,0,0,0.12)' : '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)', borderRadius: '16px', overflow: 'hidden', background: 'white', padding: '1.5rem', textAlign: 'center', border: 'none', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '400px' }}>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', marginBottom: '1rem', background: '#f8fafc', borderRadius: '12px' }}>
        {isContentPdf ? (<div style={{ color: '#ef4444', display: 'flex', flexDirection: 'column', alignItems: 'center' }}><FaFilePdf size={90} /><span style={{ marginTop: '0.5rem', fontSize: '1rem', color: '#64748b' }}>PDF Document</span></div>) : (<img src={cert.image} alt={cert.title} style={{ maxWidth: '100%', maxHeight: '230px', objectFit: 'contain' }} />)}
      </div>
      <div>
        <h3 style={{ color: THEME.text, fontSize: '1.3rem', marginBottom: '0.5rem', fontWeight: 600 }}>{cert.title}</h3>
        <p className="click-hint" style={{ color: THEME.secondary, fontSize: '1rem', fontWeight: '500', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>{isHovered ? <><FaGlobe /> {t('clickToView')}</> : t('clickToView')}</p>
      </div>
    </div>
  );
};

const NewsSection = ({ t, newsList, newsLoading, currentIndex, onPrevious, onNext, onClick, onDotClick, isPaused, setIsPaused, currentLang }) => (
  <section className="news-section full-section" style={{ background: 'linear-gradient(180deg, #ffffff 0%, #f1f5f9 100%)', padding: '6rem 2rem', position: 'relative', overflow: 'hidden' }}>
    <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
      <div style={{ textAlign: 'center', marginBottom: '4rem' }}><h2 style={{ fontSize: '3rem', fontWeight: 800, color: THEME.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', marginBottom: '0.5rem' }}><FaNewspaper /> {t('newsAndActivities')}</h2><div style={{ width: '80px', height: '6px', background: THEME.gradient, margin: '1rem auto', borderRadius: '3px' }} /></div>
      {newsLoading ? <LoadingSpinner t={t} /> : newsList.length === 0 ? <NoNewsPlaceholder t={t} /> : (
        <div onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)} style={{ position: 'relative', maxWidth: '1100px', margin: '0 auto' }}>
          <div className="news-card-shell" style={{ background: 'white', borderRadius: '24px', boxShadow: '0 20px 50px rgba(0,0,0,0.1)', overflow: 'hidden', position: 'relative', display: 'flex', flexDirection: 'column' }}>
            {/* key = id ของข่าว เพื่อให้รูปที่เลือกไว้รีเซ็ตกลับเป็นรูปแรกเมื่อสไลด์เปลี่ยนข่าว */}
            <NewsCarousel key={newsList[currentIndex]?.id ?? currentIndex} news={newsList[currentIndex]} onClick={onClick} t={t} currentLang={currentLang} />
          </div>
          <button onClick={onPrevious} aria-label="Previous" style={{ position: 'absolute', top: '50%', left: '-30px', transform: 'translateY(-50%)', width: '60px', height: '60px', borderRadius: '50%', border: 'none', background: 'white', color: THEME.primary, boxShadow: '0 4px 15px rgba(0,0,0,0.1)', cursor: 'pointer', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', transition: 'all 0.3s ease' }} onMouseEnter={(e) => { e.target.style.transform = 'translateY(-50%) scale(1.1)'; e.target.style.background = THEME.primary; e.target.style.color = 'white'; }} onMouseLeave={(e) => { e.target.style.transform = 'translateY(-50%) scale(1)'; e.target.style.background = 'white'; e.target.style.color = THEME.primary; }}><FaChevronLeft /></button>
          <button onClick={onNext} aria-label="Next" style={{ position: 'absolute', top: '50%', right: '-30px', transform: 'translateY(-50%)', width: '60px', height: '60px', borderRadius: '50%', border: 'none', background: 'white', color: THEME.primary, boxShadow: '0 4px 15px rgba(0,0,0,0.1)', cursor: 'pointer', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', transition: 'all 0.3s ease' }} onMouseEnter={(e) => { e.target.style.transform = 'translateY(-50%) scale(1.1)'; e.target.style.background = THEME.primary; e.target.style.color = 'white'; }} onMouseLeave={(e) => { e.target.style.transform = 'translateY(-50%) scale(1)'; e.target.style.background = 'white'; e.target.style.color = THEME.primary; }}><FaChevronRight /></button>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '2.5rem' }}>
            {newsList.map((_, idx) => (<button key={idx} onClick={() => onDotClick(idx)} style={{ width: idx === currentIndex ? '35px' : '12px', height: '12px', borderRadius: '12px', border: 'none', background: idx === currentIndex ? THEME.gradient : '#cbd5e1', cursor: 'pointer', transition: 'all 0.3s ease', padding: 0 }} aria-label={`Go to news ${idx + 1}`} />))}
          </div>
        </div>
      )}
    </div>
  </section>
);

const LoadingSpinner = ({ t }) => (<div style={{ textAlign: 'center', padding: '4rem', color: THEME.accent }}><div className="loading-spinner" style={{ width: '60px', height: '60px', border: '5px solid #f3f3f3', borderTop: `5px solid ${THEME.primary}`, borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 1.5rem' }} /><p style={{ fontSize: '1.2rem' }}>{t('loadingNews')}</p></div>);
const NoNewsPlaceholder = ({ t }) => (<div style={{ textAlign: 'center', padding: '5rem', background: '#f5f7fa', borderRadius: '20px' }}><FaNewspaper style={{ fontSize: '5rem', color: '#999', marginBottom: '1.5rem' }} /><p style={{ fontSize: '1.5rem' }}>{t('noNews')}</p></div>);

// จำนวนรูปสูงสุดที่โชว์ในคอลลาจ ที่เหลือจะรวมเป็น +N บนรูปสุดท้าย
const COLLAGE_MAX = 5;

// ผังคอลลาจตามจำนวนรูปที่แสดง (rows = ความสูงแต่ละแถว, spans = รูปไหนกินกี่คอลัมน์)
const getCollageLayout = (n) => {
  if (n <= 1) return { rows: '1fr', spans: ['span 2'] };
  if (n === 2) return { rows: '1fr', spans: ['span 1', 'span 1'] };
  if (n === 3) return { rows: '1.2fr 1fr', spans: ['span 2', 'span 1', 'span 1'] };
  if (n === 4) return { rows: '1fr 1fr', spans: ['span 1', 'span 1', 'span 1', 'span 1'] };
  return { rows: '1.3fr 1fr 1fr', spans: ['span 2', 'span 1', 'span 1', 'span 1', 'span 1'] };
};

const NewsCollage = ({ news, images, onOpen, t }) => {
  const visible = images.slice(0, COLLAGE_MAX);
  const remaining = images.length - visible.length;
  const { rows, spans } = getCollageLayout(visible.length);

  return (
    <div className="news-collage" style={{ gridTemplateRows: rows }}>
      {visible.map((img, i) => {
        const isLast = i === visible.length - 1;
        const showMore = isLast && remaining > 0;
        return (
          <div
            key={`${img}-${i}`}
            className="news-collage-tile"
            style={{ gridColumn: spans[i] }}
            onClick={(e) => { e.stopPropagation(); onOpen(i); }}
          >
            <img src={img} alt={`${news.title} ${i + 1}`} loading="lazy" onError={(e) => { e.target.src = '/assets/news1.jpg'; }} />

            {/* แผ่น +N ทับรูปสุดท้าย กดแล้วเปิดดูรูปที่เหลือ */}
            {showMore && (
              <div className="news-collage-more">
                <span className="more-count">+{remaining}</span>
                <span className="more-label">{t('viewAllPhotos', 'ดูรูปทั้งหมด')}</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const NewsCarousel = ({ news, onClick, t, currentLang }) => {
  const images = getNewsImages(news);
  const list = images.length > 0 ? images : ['/assets/news1.jpg'];
  const hasMany = list.length > 1;

  return (
  <div className="news-slide-content" onClick={() => onClick(0)} style={{ cursor: 'pointer', display: 'flex', flexDirection: 'row', height: '100%', animation: 'fadeIn 0.5s ease' }}>
    <div className="news-media-col" style={{ flex: '1.2', position: 'relative' }}>

      <NewsCollage news={news} images={list} onOpen={onClick} t={t} />

      {/* ป้ายวันที่ + จำนวนรูป ลอยอยู่เหนือคอลลาจ */}
      <div style={{ position: 'absolute', top: '1.5rem', left: '1.5rem', background: 'rgba(255,255,255,0.95)', padding: '0.6rem 1.2rem', borderRadius: '30px', fontSize: '1rem', fontWeight: 'bold', color: THEME.primary, display: 'flex', alignItems: 'center', gap: '0.5rem', boxShadow: '0 4px 15px rgba(0,0,0,0.15)', zIndex: 3, pointerEvents: 'none' }}>
        <FaCalendarAlt /> {news.date}
      </div>

      {hasMany && (
        <div style={{ position: 'absolute', top: '1.5rem', right: '1.5rem', background: 'rgba(15,23,42,0.8)', color: 'white', padding: '0.5rem 1rem', borderRadius: '30px', fontSize: '0.9rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.5rem', backdropFilter: 'blur(4px)', zIndex: 3, pointerEvents: 'none' }}>
          <FaImages /> {list.length} {t('photos', 'รูป')}
        </div>
      )}
    </div>
    <div className="news-info" style={{ flex: '1', padding: '3.5rem', display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: 'left' }}>
      <h3 style={{ fontSize: '2rem', fontWeight: 800, color: THEME.text, marginBottom: '1.5rem', lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{news[`title_${currentLang}`] || news.title}</h3>
      <p style={{ color: '#64748b', fontSize: '1.15rem', lineHeight: 1.7, marginBottom: '2.5rem', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{news[`description_${currentLang}`] || news.description}</p>
      <div style={{ marginTop: 'auto' }}><span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.8rem', color: 'white', background: THEME.primary, padding: '1rem 2rem', borderRadius: '50px', fontWeight: 700, fontSize: '1.1rem', transition: 'all 0.3s ease', boxShadow: '0 6px 20px rgba(30, 58, 138, 0.25)' }}>{t('readMore')} <FaArrowRight /></span></div>
    </div>
  </div>
  );
};

// 🛑 คอมเมนต์ส่วนประกอบ (Component) นี้ไว้ชั่วคราว
/*
const ClientsSection = ({ t }) => (<section className="clients-section full-section" style={{ background: '#ffffff', padding: '6rem 0' }}><div style={{ textAlign: 'center', marginBottom: '4rem' }}><h2 style={{ color: THEME.primary, fontSize: '3rem', fontWeight: '800', marginBottom: '1rem' }}><FaHandshake style={{ marginRight: '15px' }} /> {t('trustedClients')}</h2><p style={{ color: '#64748b', fontSize: '1.2rem' }}>{t('trustedClientsDesc')}</p><div style={{ width: '80px', height: '6px', background: THEME.gradient, margin: '2rem auto', borderRadius: '3px' }} /></div><div className="clients-slider" style={{ overflow: 'hidden', padding: '2rem 0' }}><div className="clients-track" style={{ display: 'flex', gap: '4rem', animation: 'scroll 30s linear infinite' }}>{[...TRUSTED_CLIENTS, ...TRUSTED_CLIENTS].map((client, index) => (<div key={`${client.id}-${index}`} className="client-logo" style={{ flex: '0 0 auto', width: '200px', height: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center', filter: 'grayscale(0%)', transition: 'all 0.3s ease', opacity: 0.9, cursor: 'pointer' }} onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.opacity = '1'; e.currentTarget.style.filter = 'drop-shadow(0 10px 20px rgba(0,0,0,0.1))'; }} onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.filter = 'none'; }}><img src={client.logo} alt={client.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} /></div>))}</div></div><style>{`@keyframes scroll { 0% { transform: translateX(0); } 100% { transform: translateX(calc(-200px * ${TRUSTED_CLIENTS.length} - 4rem * ${TRUSTED_CLIENTS.length})); } }`}</style></section>);
*/

const ContactSection = ({ t }) => (
  <section className="contact-section">
    <div className="contact-left">
      <h2 style={{ color: THEME.primary, fontSize: '2.5rem' }}><FaMailBulk /> {t('contactUs')}</h2>
      <p className="contact-info" style={{ fontSize: '1.1rem' }}>{t('contactDesc')}</p>
      <div className="contact-details">
        {[
          { icon: <FaMapMarkerAlt />, text: <span style={{ whiteSpace: 'pre-line' }}>{t('address')}</span> },
          { icon: <FaFileInvoice />, text: t('taxId') },
          { icon: <FaPhoneAlt />, text: <span>{t('phone')} <a href="tel:0906187997" style={{ color: THEME.primary }}>090 618 7997</a></span> },
          { icon: <FaLine />, text: <span>{t('Line OA:')} <a href="https://line.me/ti/p/~@730aicdq" target="_blank" rel="noopener noreferrer" style={{ color: THEME.primary }}>ASCENT Medical</a></span> },
          { icon: <FaEnvelope />, text: <span>{t('email')} <a href="mailto:ascentcorp.info@gmail.com" style={{ color: THEME.primary }}>ascentcorp.info@gmail.com</a></span> },
          { icon: <FaClock />, text: t('businessHours') }
        ].map((item, idx) => (
          <p key={idx} className="contact-info">
            <span className="icon" style={{ color: THEME.primary, fontSize: '1.3rem' }}>{item.icon}</span>{' '}
            <span style={{ fontSize: '1.1rem' }}>{item.text}</span>
          </p>
        ))}
      </div>
      <div className="contact-socials">
        <h3 style={{ fontSize: '1.5rem' }}>{t('followUs')}</h3>
        <div className="social-buttons">
          <a href="https://www.facebook.com/p/ART-Medical-61576144820349/" aria-label="Facebook" style={{ color: '#1877F2', fontSize: '2rem' }}><FaFacebook /></a>
          <a href="https://line.me/ti/p/~@730aicdq" aria-label="Line" style={{ color: '#00B900', fontSize: '2rem' }}><FaLine /></a>
        </div>
      </div>
    </div>
    <div className="contact-right">
      <iframe title="map" src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d944.6014592541595!2d98.9594083696149!3d18.735400868942563!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x30da31004afe6169%3A0xd9e0ad6904e71de2!2sAscent%20Corporation%20co.%2C%20ltd!5e0!3m2!1sth!2sth!4v1775278384870!5m2!1sth!2sth" allowFullScreen loading="lazy" style={{ border: 0, width: '100%', height: '100%' }} />
    </div>
  </section>
);

const ProductVideoSection = () => {
  const videoId = "xg-S6TZqigg";

  return (
    <section style={{ 
      padding: '40px 20px', 
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      background: 'transparent'
    }}>
      <div style={{ 
        width: '100%',
        maxWidth: '850px', 
        aspectRatio: '16 / 9',
        position: 'relative',
        borderRadius: '20px', 
        overflow: 'hidden',
        boxShadow: '0 15px 45px rgba(0, 174, 239, 0.1)',
        backgroundColor: '#000' // กันขอบกระพริบตอนโหลด
      }}>
        <iframe
          // 🟢 เพิ่มพารามิเตอร์เพื่อซ่อน UI ทั้งหมดและวนลูป
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&controls=0&loop=1&playlist=${videoId}&showinfo=0&rel=0&modestbranding=1&iv_load_policy=3&disablekb=1`}
          title="Product Video"
          frameBorder="0"
          allow="autoplay; encrypted-media"
          style={{
            position: 'absolute',
            top: '-2px', // 🟢 ดันขึ้นเล็กน้อยเพื่อซ่อนขอบบน
            left: '-2px', // 🟢 ดันซ้ายเล็กน้อย
            width: 'calc(100% + 4px)', // 🟢 ขยายเกินขนาดจริงเล็กน้อยเพื่อ "Crop" ขอบดำหรือเส้นจางๆ
            height: 'calc(100% + 4px)',
            pointerEvents: 'none', // 🟢 ป้องกันการกดหยุด (Pause)
            border: 'none'
          }}
        />
      </div>
    </section>
  );
};

const HomePage = ({ t, currentLang, certificates, certLoading, newsList, newsLoading, products, currentNewsIndex, onGoToPreviousNews, onGoToNextNews, onCertificateClick, onNewsClick, onDotClick, isPaused, setIsPaused, onServiceClick }) => {
  if (!t) return <div style={{ padding: '50px', textAlign: 'center' }}>Loading...</div>;
  
  return (
  <>
    <HeroSection t={t} />
    <AboutSection t={t} /> 
    <ProductVideoSection /> 
    <ProductsSection t={t} products={products} currentLang={currentLang} /> 
    <ServicesSection t={t} onServiceClick={onServiceClick} /> 
    <CertificatesSection t={t} certificates={certificates} certLoading={certLoading} onCertificateClick={onCertificateClick} />
    <NewsSection t={t} currentLang={currentLang} newsList={newsList} newsLoading={newsLoading} currentIndex={currentNewsIndex} onPrevious={onGoToPreviousNews} onNext={onGoToNextNews} onClick={onNewsClick} onDotClick={onDotClick} isPaused={isPaused} setIsPaused={setIsPaused} />
    
    {/* 🛑 คอมเมนต์การดึงมาแสดงผลไว้ชั่วคราว */}
    {/* <ClientsSection t={t} /> */}
    
    <ContactSection t={t} />
  </>
)};

function AppContent() {
  const { t, i18n } = useTranslation(); 
  const currentLang = i18n.language ? i18n.language.split('-')[0] : 'th'; 

  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);

  const [selectedImage, setSelectedImage] = useState(null);
  const [modalType, setModalType] = useState('certificate');
  const [galleryStart, setGalleryStart] = useState(0);

  const [newsList, setNewsList] = useState([]);
  const [newsLoading, setNewsLoading] = useState(true);
  const [currentNewsIndex, setCurrentNewsIndex] = useState(0);
  const [isNewsPaused, setIsNewsPaused] = useState(false);
  const [certificates, setCertificates] = useState([]);
  const [certLoading, setCertLoading] = useState(true);
  const [recommendedProducts, setRecommendedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  

  const toggleMenu = useCallback(() => setMenuOpen(prev => !prev), []);
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  const closeModal = useCallback(() => { setSelectedImage(null); setModalType('certificate'); setGalleryStart(0); }, []);
  
  const goToPreviousNews = useCallback(() => { if (newsList.length === 0) return; setCurrentNewsIndex(prev => (prev === 0 ? newsList.length - 1 : prev - 1)); }, [newsList.length]);
  const goToNextNews = useCallback(() => { if (newsList.length === 0) return; setCurrentNewsIndex(prev => (prev === newsList.length - 1 ? 0 : prev + 1)); }, [newsList.length]);
  const goToSpecificNews = (index) => setCurrentNewsIndex(index);
  const handleCertificateClick = (cert) => { setSelectedImage(cert); setModalType('certificate'); };
  // startIndex = รูปที่ผู้ใช้กดในคอลลาจ เพื่อให้โมดัลเปิดตรงรูปนั้น
  const handleNewsClick = (startIndex = 0) => {
    setSelectedImage(newsList[currentNewsIndex]);
    setModalType('news');
    setGalleryStart(typeof startIndex === 'number' ? startIndex : 0);
  };
  const handleServiceClick = (service) => { setSelectedImage(service); setModalType('service'); };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: newsData } = await supabase.from('news_activities').select('*').order('id', { ascending: false });
        if (newsData) setNewsList(newsData);
        const { data: certData } = await supabase.from('certificates').select('*').order('id', { ascending: true });
        if (certData) setCertificates(certData);
        const { data: productData } = await supabase.from('products').select('*').eq('is_recommended', true).limit(9).order('id', { ascending: true });
        if (productData) setRecommendedProducts(productData);
        const { data: catData } = await supabase.from('categories').select('*').order('id', { ascending: true });
        if (catData) setCategories(catData);
      } catch (err) { console.error('Error:', err); } 
      finally { setNewsLoading(false); setCertLoading(false); }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (newsList.length === 0 || isNewsPaused) return;
    const interval = setInterval(() => setCurrentNewsIndex(prev => prev === newsList.length - 1 ? 0 : prev + 1), 5000);
    return () => clearInterval(interval);
  }, [newsList.length, isNewsPaused]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setScrolled(currentScrollY > 50);
      setHidden(currentScrollY > lastScrollY && currentScrollY > 100);
      setLastScrollY(currentScrollY);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  useEffect(() => { 
    if (selectedImage) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; }; 
  }, [selectedImage]);

  return (
    <div className="App" style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', width: '100%', fontFamily: "'Prompt', sans-serif" }}>
      <Navbar 
        menuOpen={menuOpen} 
        toggleMenu={toggleMenu} 
        closeMenu={closeMenu} 
        scrolled={scrolled} 
        hidden={hidden} 
        categories={categories} 
      />
      <main style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Routes>
          <Route path="/" element={
            <HomePage 
              t={t} 
              currentLang={currentLang}
              certificates={certificates} 
              certLoading={certLoading} 
              newsList={newsList} 
              newsLoading={newsLoading} 
              products={recommendedProducts} 
              currentNewsIndex={currentNewsIndex} 
              onGoToPreviousNews={goToPreviousNews} 
              onGoToNextNews={goToNextNews} 
              onDotClick={goToSpecificNews} 
              onCertificateClick={handleCertificateClick} 
              onNewsClick={handleNewsClick} 
              onServiceClick={handleServiceClick} 
              isPaused={isNewsPaused} 
              setIsPaused={setIsNewsPaused} 
            />
          } />
          <Route path="/services" element={<Services />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/products" element={<Products />} />
          
          {/* 🟢 เพิ่ม Route สำหรับหน้ารายละเอียดสินค้าตรงนี้ครับ */}
          <Route path="/product/:id" element={<ProductDetail />} />
          
          <Route path="/admin" element={<AdminPage />} />
        </Routes><FloatingContact />
      </main>
      <CookieConsent />
      <ImageModal image={selectedImage} onClose={closeModal} type={modalType} t={t} currentLang={currentLang} galleryStart={galleryStart} />
      <Footer />
    </div>
  );
}

function App() {
  return (
    <Router>
      <ScrollToTop />
      <AppContent />
    </Router>
  );
}

export default App;